import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, statusPill, gridActionButton, newRecordButtonStyle, getODataRows, resolveODataTotalCount } from './listPageStyles';
import { RepresentativeGroupsPage } from './RepresentativeGroupsPage';
import { RepresentativeTypesPage } from './RepresentativeTypesPage';

const API_BASE = '/api/representatives';

const EMPTY_FORM = {
  textId: '',
  name: '',
  erpId: '',
  reference: '',
  groupId: '',
  salesManagerId: '',
  status: '',
  vendorId: '',
  workingType: '',
};

const LOOKUP_SORT_FIELDS = ['GroupLabel', 'VendorLabel', 'SalesManagerLabel'];
const representativeTabs = [
  { key: 'representatives', label: 'Temsilciler' },
  { key: 'groups', label: 'Temsilci Grubu' },
  { key: 'types', label: 'Temsilci Tipi' },
];
const representativeTabStyles = {
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { minHeight: 36, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #fff)', color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function toNullableGuid(value) {
  return value ? value : null;
}

function toRequiredInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function compareText(a, b, direction) {
  const left = (a || '').toString().toLocaleLowerCase('tr-TR');
  const right = (b || '').toString().toLocaleLowerCase('tr-TR');
  const result = left.localeCompare(right, 'tr');
  return direction === 'asc' ? result : -result;
}

export function RepresentativesPageLegacy() {
  const [activeDefinition, setActiveDefinition] = useState('representatives');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [representativeGroups, setRepresentativeGroups] = useState([]);

  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const getVendorLabel = useCallback((vendorId) => {
    const vendor = vendors.find(item => item.id === vendorId);
    if (!vendor) return '-';
    return vendor.name ? `${vendor.textId || ''} - ${vendor.name}`.trim().replace(/^- /, '') : vendor.textId || '-';
  }, [vendors]);

  const getGroupLabel = useCallback((groupId) => {
    const group = representativeGroups.find(item => item.id === groupId);
    if (!group) return '-';
    return group.description ? `${group.textId || ''} - ${group.description}`.trim().replace(/^- /, '') : group.textId || '-';
  }, [representativeGroups]);

  const getSalesManagerLabel = useCallback((salesManagerId) => {
    const manager = salesManagers.find(item => item.id === salesManagerId);
    if (!manager) return '-';
    return manager.description ? `${manager.textId || ''} - ${manager.description}`.trim().replace(/^- /, '') : manager.textId || '-';
  }, [salesManagers]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/vendors/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/representativegroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/salesmanagers/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([vendorData, groupData, salesManagerData]) => {
      setVendors(unwrapLookupItems(vendorData));
      setRepresentativeGroups(unwrapLookupItems(groupData));
      setSalesManagers(unwrapLookupItems(salesManagerData));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(TextId),'${q}') or contains(tolower(Name),'${q}') or contains(tolower(ErpId),'${q}') or contains(tolower(Reference),'${q}') or contains(tolower(WorkingType),'${q}'))`;
      }

      const isLookupSort = LOOKUP_SORT_FIELDS.includes(sortField);
      const url = buildODataUrl(API_BASE, {
        pageSize: isLookupSort ? 100000 : pageSize,
        currentPage: isLookupSort ? 1 : currentPage,
        sortField: isLookupSort ? 'TextId' : sortField,
        sortDir,
        filterParams,
      });

      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');
      const data = await res.json();
      const allRows = getODataRows(data);

      if (isLookupSort) {
        const skip = (currentPage - 1) * pageSize;
        const sortedRows = [...allRows].sort((a, b) => (
          sortField === 'VendorLabel'
            ? compareText(getVendorLabel(a.vendorId), getVendorLabel(b.vendorId), sortDir)
            : sortField === 'GroupLabel'
              ? compareText(getGroupLabel(a.groupId), getGroupLabel(b.groupId), sortDir)
              : compareText(getSalesManagerLabel(a.salesManagerId), getSalesManagerLabel(b.salesManagerId), sortDir)
        ));
        setRows(sortedRows.slice(skip, skip + pageSize));
        setTotalCount(sortedRows.length);
      } else {
        setRows(allRows);
        setTotalCount(await resolveODataTotalCount({ data, rows: allRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders() }));
      }
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [filterText, sortField, sortDir, pageSize, currentPage, getVendorLabel, getGroupLabel, getSalesManagerLabel]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilterSearch = () => {
    setFilterText(filterInput);
    setCurrentPage(1);
  };

  const clearFilter = () => {
    setFilterText('');
    setFilterInput('');
    setCurrentPage(1);
  };

  const rowToForm = (row) => ({
    textId: row.textId || '',
    name: row.name || '',
    erpId: row.erpId || '',
    reference: row.reference || '',
    groupId: row.groupId || '',
    salesManagerId: row.salesManagerId || '',
    status: row.status ?? '',
    vendorId: row.vendorId || '',
    workingType: row.workingType || '',
  });

  const openNew = () => {
    setForm(EMPTY_FORM);
    setSelected(null);
    setModal('new');
  };

  const openEdit = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('edit');
  };

  const openView = (row = selected) => {
    if (!row) return;
    setSelected(row);
    setForm(rowToForm(row));
    setModal('view');
  };

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    if (!form.textId.trim()) {
      showToast('error', 'Temsilci kodu bos olamaz');
      return;
    }
    if (!form.name.trim()) {
      showToast('error', 'Temsilci adi bos olamaz');
      return;
    }
    if (!form.vendorId) {
      showToast('error', 'Tedarikci secimi zorunludur');
      return;
    }
    if (!form.workingType.trim()) {
      showToast('error', 'Calisma tipi bos olamaz');
      return;
    }

    const payload = {
      textId: form.textId,
      name: form.name,
      erpId: form.erpId || null,
      reference: form.reference || null,
      groupId: toNullableGuid(form.groupId),
      salesManagerId: toNullableGuid(form.salesManagerId),
      status: toRequiredInt(form.status),
      vendorId: form.vendorId,
      workingType: form.workingType,
    };

    try {
      const isNew = modal === 'new';
      const res = await fetch(`${API_BASE}/${isNew ? 'Add' : 'Update'}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(isNew ? payload : { id: selected.id, ...payload }),
      });
      if (!res.ok) throw new Error(isNew ? 'Kayit eklenemedi' : 'Kayit guncellenemedi');
      showToast('success', isNew ? 'Temsilci basariyla eklendi' : 'Temsilci basariyla guncellendi');
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Islem sirasinda hata olustu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Kayit silinemedi');
      showToast('success', 'Temsilci basariyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme islemi sirasinda hata olustu');
      setDeleteConfirm(false);
    }
  };

  const isView = modal === 'view';
  const inputStyle = isView ? s.inputView : s.input;
  const selectStyle = isView ? s.selectView : s.select;
  const columns = [
    { field: 'textId', headerName: 'Kod', flex: 1, minWidth: 130 },
    { field: 'name', headerName: 'Ad', flex: 1.3, minWidth: 180 },
    { field: 'vendorLabel', headerName: 'Tedarikçi', flex: 1.3, minWidth: 190, sortable: false, renderCell: (params) => getVendorLabel(params.row.vendorId) },
    { field: 'groupLabel', headerName: 'Grup', flex: 1.2, minWidth: 170, sortable: false, renderCell: (params) => getGroupLabel(params.row.groupId) },
    { field: 'salesManagerLabel', headerName: 'Satış Müdürü', flex: 1.2, minWidth: 170, sortable: false, renderCell: (params) => getSalesManagerLabel(params.row.salesManagerId) },
    { field: 'workingType', headerName: 'Çalışma Tipi', flex: 1, minWidth: 140 },
    { field: 'status', headerName: 'Durum', width: 130, renderCell: (params) => statusPill(params.row.status) },
    {
      field: 'actions',
      headerName: 'İşlemler',
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Görüntüle</button>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Değiştir</button>
          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }} onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}>Sil</button>
        </div>
      ),
    },
  ];
  const renderDefinitionTabs = () => (
    <div style={representativeTabStyles.tabBar}>
      {representativeTabs.map(tab => (
        <button
          key={tab.key}
          type="button"
          style={{ ...representativeTabStyles.tab, ...(activeDefinition === tab.key ? representativeTabStyles.tabActive : {}) }}
          onClick={() => setActiveDefinition(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeDefinition === 'groups') {
    return <div style={s.container}>{renderDefinitionTabs()}<RepresentativeGroupsPage /></div>;
  }

  if (activeDefinition === 'types') {
    return <div style={s.container}>{renderDefinitionTabs()}<RepresentativeTypesPage /></div>;
  }

  return (
    <div style={s.container}>
      {renderDefinitionTabs()}
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Temsilci Yonetimi</h2>
          <p style={s.pageSubtitle}>Temsilci kayitlarini filtreleyin, siralayin ve yonetin.</p>
        </div>
        <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Temsilci</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Kod, ad, ERP, referans veya calisma tipi ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={event => { if (event.key === 'Enter') handleFilterSearch(); }}
          />
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        exportFileName="temsilciler"
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'TextId'); setSortDir(direction || 'asc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {modal === 'new' ? 'Yeni Temsilci' : modal === 'edit' ? 'Temsilci Duzenle' : 'Temsilci Detay'}
              </h3>
              <button style={s.closeBtn} onClick={closeModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kod *</label>
                  <input style={inputStyle} value={form.textId} onChange={e => setForm(current => ({ ...current, textId: e.target.value }))} readOnly={isView} autoFocus />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ad *</label>
                  <input style={inputStyle} value={form.name} onChange={e => setForm(current => ({ ...current, name: e.target.value }))} readOnly={isView} />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Tedarikci *</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={getVendorLabel(form.vendorId)} />
                  ) : (
                    <select style={selectStyle} value={form.vendorId} onChange={e => setForm(current => ({ ...current, vendorId: e.target.value }))}>
                      <option value="">Seciniz</option>
                      {vendors.map(vendor => (
                        <option key={vendor.id} value={vendor.id}>{vendor.textId}{vendor.name ? ` - ${vendor.name}` : ''}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Satis Muduru</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={getSalesManagerLabel(form.salesManagerId)} />
                  ) : (
                    <select style={selectStyle} value={form.salesManagerId} onChange={e => setForm(current => ({ ...current, salesManagerId: e.target.value }))}>
                      <option value="">Seciniz</option>
                      {salesManagers.map(manager => (
                        <option key={manager.id} value={manager.id}>{manager.textId}{manager.description ? ` - ${manager.description}` : ''}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Temsilci Grubu</label>
                  {isView ? (
                    <input style={s.inputView} readOnly value={getGroupLabel(form.groupId)} />
                  ) : (
                    <select style={selectStyle} value={form.groupId} onChange={e => setForm(current => ({ ...current, groupId: e.target.value }))}>
                      <option value="">Seciniz</option>
                      {representativeGroups.map(group => (
                        <option key={group.id} value={group.id}>{group.textId}{group.description ? ` - ${group.description}` : ''}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Calisma Tipi *</label>
                  <input style={inputStyle} value={form.workingType} onChange={e => setForm(current => ({ ...current, workingType: e.target.value }))} readOnly={isView} />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <input style={inputStyle} value={form.status} onChange={e => setForm(current => ({ ...current, status: e.target.value }))} readOnly={isView} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>ERP Kodu</label>
                  <input style={inputStyle} value={form.erpId} onChange={e => setForm(current => ({ ...current, erpId: e.target.value }))} readOnly={isView} />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Referans</label>
                  <input style={inputStyle} value={form.reference} onChange={e => setForm(current => ({ ...current, reference: e.target.value }))} readOnly={isView} />
                </div>
                <div style={s.formGroup} />
              </div>
            </div>
            <div style={s.modalFooter}>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
              <button style={s.btnSecondary} onClick={closeModal}>{isView ? 'Kapat' : 'Iptal'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayi</h3>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{selected?.name}"</strong> temsilcisini silmek istediginize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Iptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
